'use client';

import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { usePathname, useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import { useAuth } from '@/app/Components/AuthProvider';
import { useTheme } from '@/app/Components/ThemeProvider';
import { useUiPreferences } from '@/app/Components/UiPreferencesProvider';
import { useAssistantPopup } from '@/app/Components/Assistant/AssistantContext';
import { useCoursePeek } from '@/app/Components/CoursePeek/CoursePeekProvider';
import { buildAssistantOpenContext, buildAssistantWorkspaceHref } from '@/app/Components/Assistant/pageContext';
import { trackEvent } from '@/app/lib/posthog';
import { recordRecentSelection, clearRecentSearchItems } from '@/app/lib/search/recentItems';
import type { SearchResult, SearchScope, SearchAction } from '@/app/lib/search/types';
import {
  createGuestAssistantLoginHref,
  routeAssistantLaunch,
} from './assistantHandoff';

const SearchDialog = dynamic(() => import('./SearchDialog'), { ssr: false });

function focusDestinationHeadingAfterNavigation(href: string) {
  const destinationPath = new URL(href, window.location.origin).pathname;
  let remainingFrames = 120;
  const focusWhenReady = () => {
    const destinationHeading = window.location.pathname === destinationPath
      ? document.querySelector<HTMLElement>('#main-scroll-container h1, #main-scroll-container [role="heading"]')
      : null;
    if (!destinationHeading) {
      if (remainingFrames-- > 0) window.requestAnimationFrame(focusWhenReady);
      return;
    }
    destinationHeading.tabIndex = -1;
    destinationHeading.focus({ preventScroll: true });
  };
  window.requestAnimationFrame(focusWhenReady);
}

type UniversalSearchContextValue = {
  isOpen: boolean;
  entryMode: 'launcher' | 'search' | 'assistant';
  openSearch: (options?: {
    mode?: 'launcher' | 'search' | 'assistant';
    initialQuery?: string;
    returnFocusTo?: HTMLElement | null;
  }) => void;
  closeSearch: () => void;
  registerPageItems: (prefix: string, items: SearchResult[]) => void;
  unregisterPageItems: (prefix: string) => void;
};

const UniversalSearchContext = createContext<UniversalSearchContextValue | undefined>(
  undefined
);

export function UniversalSearchProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { isSidebarPinned, toggleSidebarPin, activeSemester } = useUiPreferences();
  const coursePeek = useCoursePeek();
  const { openAssistant } = useAssistantPopup();

  const [isOpen, setIsOpen] = useState(false);
  const [entryMode, setEntryMode] = useState<'launcher' | 'search' | 'assistant'>('launcher');
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<SearchScope>('all');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [restoreFocusOnClose, setRestoreFocusOnClose] = useState(true);
  const [pageItemsByPrefix, setPageItemsByPrefix] = useState<Record<string, SearchResult[]>>({});
  const pageItems = useMemo(
    () => Object.values(pageItemsByPrefix).flat(),
    [pageItemsByPrefix]
  );
  const [pendingConfirm, setPendingConfirm] = useState<{
    result: SearchResult;
    action: SearchAction;
    message: string;
  } | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const searchOpenerRef = useRef<HTMLElement | null>(null);
  const searchStartRef = useRef<number>(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeSearch = useCallback((options?: { restoreFocus?: boolean }) => {
    const opener = searchOpenerRef.current;
    searchOpenerRef.current = null;
    const shouldRestoreFocus = options?.restoreFocus !== false;
    setRestoreFocusOnClose(shouldRestoreFocus);
    setIsOpen(false);
    setPendingConfirm(null);
    abortRef.current?.abort();
    setResults([]);
    setLoading(false);
    if (shouldRestoreFocus) {
      requestAnimationFrame(() => {
        if (opener?.isConnected) {
          opener.focus({ preventScroll: true });
        }
      });
    }
  }, []);

  const openSearch = useCallback((options?: {
    mode?: 'launcher' | 'search' | 'assistant';
    initialQuery?: string;
    returnFocusTo?: HTMLElement | null;
  }) => {
    setRestoreFocusOnClose(true);
    searchOpenerRef.current = options?.returnFocusTo
      ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    const nextMode = options?.mode ?? 'search';
    setEntryMode(nextMode);

    if (nextMode === 'launcher') {
      setQuery('');
      setResults([]);
      setLoading(false);
    } else {
      setQuery(typeof options?.initialQuery === 'string' ? options.initialQuery : '');
      setResults([]);
      setLoading(false);
    }
    setIsOpen(true);
    setSelectedIndex(0);
  }, []);

  const openAssistantWithAuth = useCallback((context?: Record<string, unknown>) => {
    routeAssistantLaunch({
      isAuthenticated: Boolean(user),
      context,
      navigate: (href) => router.push(href),
      launchAuthenticated: (nextContext) => {
        openAssistant(buildAssistantOpenContext(nextContext));
      },
      createGuestHref: createGuestAssistantLoginHref,
    });
  }, [openAssistant, router, user]);

  const openAssistantChat = useCallback((prompt?: string, threadId?: string) => {
    const trimmed = prompt?.trim() ?? '';
    const context = buildAssistantOpenContext({
      initialPrompt: trimmed,
      autoSend: Boolean(trimmed),
      startNewChat: true,
    });
    closeSearch({ restoreFocus: false });
    const href = buildAssistantWorkspaceHref(context);
    const target = threadId ? `${href}${href.includes('?') ? '&' : '?'}threadId=${encodeURIComponent(threadId)}` : href;
    router.push(user ? target : createGuestAssistantLoginHref(trimmed));
  }, [closeSearch, router, user]);

  const runtimeActions = useMemo(
    () => ({
      navigate: (href: string) => router.push(href),
      toggleTheme,
      toggleSidebarPin,
      // Search results can open the assistant too. Merge the route snapshot
      // here so this path has the same schedule/plan/selection grounding as
      // the dock and spotlight launchers.
      openAssistant: openAssistantWithAuth,
      closeSearch,
      theme,
      isSidebarPinned,
    }),
    [
      router,
      toggleTheme,
      toggleSidebarPin,
      openAssistantWithAuth,
      closeSearch,
      theme,
      isSidebarPinned,
    ]
  );

  useEffect(() => {
    if (!isOpen) {
      abortRef.current?.abort();
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      searchStartRef.current = performance.now();
      setLoading(Boolean(query.trim()));

      void import('@/app/lib/search/searchOrchestrator').then(({ runSearch }) => {
        if (controller.signal.aborted) return;
        return runSearch(
          {
            query,
            scope,
            pathname,
            userId: user?.id,
            semester: activeSemester,
            pageItems,
            actions: runtimeActions,
            signal: controller.signal,
          },
          {
            onResults: (nextResults, { loading: isLoading }) => {
              if (controller.signal.aborted) return;
              startTransition(() => {
                setResults(nextResults);
                setLoading(isLoading);
                setSelectedIndex(0);
              });
              if (!isLoading && query.trim()) {
                trackEvent('scarlet_search', {
                  query_length: query.trim().length,
                  result_count: nextResults.length,
                  scope,
                  latency_ms: Math.round(performance.now() - searchStartRef.current),
                });
              }
            }
          }
        );
      });
    }, query.trim() ? 150 : 0);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, [query, scope, pathname, user?.id, activeSemester, pageItems, isOpen, runtimeActions]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isK = e.key.toLowerCase() === 'k';
      const mod = e.metaKey || e.ctrlKey;
      if (!mod || !isK) return;

      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (
        tag === 'textarea' ||
        (tag === 'input' &&
          (target as HTMLInputElement).type !== 'search' &&
          !(target as HTMLInputElement).dataset.scarletSearch)
      ) {
        return;
      }

      e.preventDefault();
      if (isOpen) closeSearch();
      else openSearch();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, openSearch, closeSearch]);

  const registerPageItems = useCallback((prefix: string, items: SearchResult[]) => {
    setPageItemsByPrefix((prev) => ({ ...prev, [prefix]: items }));
  }, []);

  const unregisterPageItems = useCallback((prefix: string) => {
    setPageItemsByPrefix((prev) => {
      const next = { ...prev };
      delete next[prefix];
      return next;
    });
  }, []);

  useEffect(() => {
    if (!user) clearRecentSearchItems();
  }, [user]);

  const runAction = useCallback(
    async (result: SearchResult, action?: SearchAction) => {
      const chosen = action ?? result.actions?.[0];
      const isNavigationEntry = result.kind === 'feature' && result.id.startsWith('nav-');
      // A course opens over the current page instead of leaving it.
      const courseId = (chosen?.href ?? result.href)?.match(/^\/courses\/([\d:]{6,})$/)?.[1];
      if (courseId && coursePeek && (!chosen || chosen.id === 'course-wiki')) {
        trackEvent('search:result_selected', { result_kind: result.kind, selection_source: chosen ? 'action' : 'primary' });
        recordRecentSelection(result);
        closeSearch({ restoreFocus: false });
        coursePeek.openCourse({ courseId, semester: activeSemester });
        return;
      }
      if (!chosen) {
        if (result.href) {
          trackEvent('search:result_selected', { result_kind: result.kind, selection_source: 'primary' });
          router.push(result.href);
          focusDestinationHeadingAfterNavigation(result.href);
          recordRecentSelection(result);
          closeSearch({ restoreFocus: false });
        }
        return;
      }

      if (chosen.requiresConfirmation) {
        setPendingConfirm({
          result,
          action: chosen,
          message: chosen.confirmMessage ?? `Confirm ${chosen.label}?`,
        });
        return;
      }

      const shouldCloseAfterAction = isNavigationEntry || !chosen.keepOpen;
      const destinationHref = result.href ?? chosen.href;
      const shouldFocusDestination = shouldCloseAfterAction && (isNavigationEntry || Boolean(chosen.href)) && Boolean(destinationHref);
      if (chosen.run) {
        await chosen.run();
        if (shouldFocusDestination && destinationHref) focusDestinationHeadingAfterNavigation(destinationHref);
      }
      else if (chosen.href) {
        trackEvent('search:result_selected', { result_kind: result.kind, selection_source: 'action' });
        router.push(chosen.href);
        if (shouldFocusDestination) focusDestinationHeadingAfterNavigation(chosen.href);
      }
      recordRecentSelection(result);
      if (shouldCloseAfterAction) {
        closeSearch({ restoreFocus: !shouldFocusDestination });
      }
    },
    [router, closeSearch, coursePeek, activeSemester]
  );

  const handleActivate = useCallback(
    (result: SearchResult) => {
      const primary =
        result.actions?.length === 1
          ? result.actions[0]
          : result.actions?.find((a) => a.variant === 'primary') ?? result.actions?.[0];
      void runAction(result, primary);
    },
    [runAction]
  );

  const handleQueryChange = useCallback((nextQuery: string) => {
    setQuery(nextQuery);
    setSelectedIndex(0);
    setPendingConfirm(null);
    abortRef.current?.abort();
    setResults([]);
    if (nextQuery.trim()) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, []);

  const selectedResult = results[selectedIndex] ?? null;

  const value = useMemo(
    () => ({
      isOpen,
      entryMode,
      openSearch,
      closeSearch,
      registerPageItems,
      unregisterPageItems,
    }),
    [isOpen, entryMode, openSearch, closeSearch, registerPageItems, unregisterPageItems]
  );

  return (
    <UniversalSearchContext.Provider value={value}>
      <div
        className="contents"
        data-universal-search-background=""
        inert={isOpen || undefined}
        aria-hidden={isOpen || undefined}
      >
        {children}
      </div>
      {isOpen ? <SearchDialog
        isOpen={isOpen}
        onClose={closeSearch}
        query={query}
        onQueryChange={handleQueryChange}
        results={results}
        selectedIndex={selectedIndex}
        onSelectIndex={setSelectedIndex}
        onActivate={handleActivate}
        loading={loading}
        entryMode={entryMode}
        onEntryModeChange={setEntryMode}
        scope={scope}
        onScopeChange={setScope}
        showMine={Boolean(user)}
        selectedResult={selectedResult}
        onOpenAssistantChat={openAssistantChat}
        pendingConfirm={
          pendingConfirm
            ? {
                result: pendingConfirm.result,
                actionId: pendingConfirm.action.id,
                message: pendingConfirm.message,
              }
            : null
        }
        onConfirm={() => {
          if (pendingConfirm?.action.run) void pendingConfirm.action.run();
          recordRecentSelection(pendingConfirm!.result);
          setPendingConfirm(null);
          closeSearch();
        }}
        onCancelConfirm={() => setPendingConfirm(null)}
        returnFocusTo={searchOpenerRef.current}
        restoreFocusOnClose={restoreFocusOnClose}
      /> : null}
    </UniversalSearchContext.Provider>
  );
}

export function useUniversalSearch() {
  const ctx = useContext(UniversalSearchContext);
  if (!ctx) {
    throw new Error('useUniversalSearch must be used within UniversalSearchProvider');
  }
  return ctx;
}
